import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { Component, inject, OnInit } from '@angular/core';
import { MatFormField, MatInput } from '@angular/material/input';
import { MatButton } from '@angular/material/button';
import { MatLabel } from '@angular/material/form-field';
import {
  firstValueFrom,
  from,
  Observable,
  of,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { ConsumerEntity } from '../../consumer/consumer.entity';
import { ConsumerEntityState } from '../../consumer/services/consumer.entity.service';
import { Store } from '@ngxs/store';
import { ConsumerEntityService } from '../../consumer/services/consumer.entity.service';
import { AsyncPipe, CommonModule } from '@angular/common';
import { MatOption, MatSelect } from '@angular/material/select';
import {
  normalizeManyRelations,
  normalizeOneRelation,
} from '../../core/decorators/relation.decorator';
import { SubjectEntityService } from '../../subject/services/subject.entity.service';
import { SubjectEntity } from '../../subject/subject.entity';
import { map } from 'rxjs/operators';
import { MultiComboEntitySearchComponent } from '../../utils/component/multi-combo-entity-search/multi-combo-entity-search.component';
import { MatDivider } from '@angular/material/divider';
import { LearningUnitEntity } from '../../learning-unit/learning-unit-entity';
import { LearningUnitEntityService } from '../../learning-unit/services/learning-unit.entity.service';
import { EntityStateModel } from '../../core/state/entity.state';
import { LearningSectionEntity } from '../learning-section-entity';
import { LearningSectionLayoutEntity } from '../learning-section-layout/learning-section-layout-entity';
import { LearningSectionLayoutEntityService } from '../learning-section-layout/services/learning-section-layout.entity.service';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { RelationRef } from '../../core/types/entity-types';
import { LearningSectionContainerEntity } from '../learning-section-container/learning-section-container-entity';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import { ObjectConsumerMetadataComponent } from '../../consumer/object-consumer-metadata/object-consumer-metadata.component';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'pixl-form-dialog-learning-section',
  templateUrl: './form-dialog-learning-section.component.html',
  imports: [
    CommonModule,
    MatDialogContent,
    MatFormField,
    MatDialogTitle,
    MatLabel,
    MatFormField,
    MatInput,
    MatFormField,
    MatDialogActions,
    ReactiveFormsModule,
    MatButton,
    MatSelect,
    AsyncPipe,
    MatOption,
    FormsModule,
    MultiComboEntitySearchComponent,
    MatDivider,
    MatSlideToggle,
    MatTabGroup,
    MatTab,
    AsyncPipe,
    ObjectConsumerMetadataComponent,
    TranslocoModule,
  ],
})
export class FormDialogLearningSectionComponent implements OnInit {
  private fb = inject(FormBuilder);
  private dialogRef =
    inject<MatDialogRef<FormDialogLearningSectionComponent>>(MatDialogRef);

  data? = inject<Partial<LearningSectionEntity>>(MAT_DIALOG_DATA);

  form: FormGroup;
  consumers$!: Observable<ConsumerEntity[]>;
  subjects$!: Observable<SubjectEntity[]>;
  learningUnits$!: Observable<LearningUnitEntity[]>;
  learningSectionLayouts$!: Observable<LearningSectionLayoutEntity[]>;
  selectedConsumerName$!: Observable<string>;
  // learningSectionContainers$!: Observable<LearningSectionEntity[]>;

  private store = inject(Store);
  private consumerService = inject(ConsumerEntityService);
  private subjectService = inject(SubjectEntityService);
  private learningUnitService = inject(LearningUnitEntityService);
  private learningSectionLayoutService = inject(
    LearningSectionLayoutEntityService,
  );
  // private learningSectionContainerService = inject(LearningSectionContainerEntityService);

  selectedLearningUnits: LearningUnitEntity[] = [];
  currentPageLearningUnits = 1;
  pageSizeLearningUnits = 20;
  searchQueryLearningUnits: string | null = null;

  constructor() {
    const data = this.data;

    this.form = this.fb.group({
      name: [data?.name || '', Validators.required],
      title: [data?.title || '', Validators.required],
      learningSectionType: [data?.learningSectionType || '', Validators.required],
      showCopyright: [data?.showCopyright || false],
      version: [data?.version || 1],
      consumer: [
        normalizeOneRelation<ConsumerEntity>(
          data?.consumer as RelationRef<ConsumerEntity>[],
        ),
      ],
      consumerData: this.fb.group(
        Object.fromEntries(
          Object.entries(data?.consumerData || {}).map(([key, value]) => [
            key,
            Array.isArray(value) ? [value] : value,
          ]),
        ),
      ),
      subjects: [normalizeManyRelations(data?.subjects)],
      learningUnits: [normalizeManyRelations(data?.learningUnits)],
      learningSectionLayout: [
        normalizeOneRelation(data?.learningSectionLayout),
      ],
      learningSectionContainers: [
        normalizeManyRelations(data?.learningSectionContainers),
      ],
    });
  }

  ngOnInit() {
    this.selectedConsumerName$ = this.form.get('consumer')!.valueChanges.pipe(
      startWith(this.form.get('consumer')!.value),
      switchMap((val) => {
        const id = Array.isArray(val) ? val[0] : val;
        if (!id) {
          return of('Metadaten');
        }
        return this.store.select(ConsumerEntityState.byId).pipe(
          map((fn) => fn(id)),
          map((consumer) => consumer?.name || 'Metadaten'),
        );
      }),
    );
    const learningUnitQuery = {
      'page[size]': this.pageSizeLearningUnits,
      'page[number]': this.currentPageLearningUnits,
      'sort': 'title',
    };

    this.learningUnits$ = from(
      this.learningUnitService.ensureLoaded(learningUnitQuery),
    ).pipe(
      switchMap(() =>
        this.store
          .select(this.learningUnitService.allByQuery(learningUnitQuery))
          .pipe(
            map((paginated) => {
              const combined = [
                ...this.selectedLearningUnits,
                ...paginated,
              ].filter(
                (lu, index, self) =>
                  index === self.findIndex((s) => s.id === lu.id),
              );
              return combined;
            }),
          ),
      ),
    );

    const selectedLearningUnitIds = normalizeManyRelations(
      this.data?.learningUnits,
    ) as string[];

    const state = this.store.selectSnapshot(
      this.learningUnitService.stateClass,
    ) as EntityStateModel<LearningUnitEntity>;
    this.selectedLearningUnits = selectedLearningUnitIds
      .map((id) => state.entities[id])
      .filter((e): e is LearningUnitEntity => !!e);

    this.learningUnits$ = this.store
      .select(this.learningUnitService.allByQuery(learningUnitQuery))
      .pipe(
        map((paginated) => {
          const combined = [...this.selectedLearningUnits, ...paginated].filter(
            (seq, index, self) =>
              index === self.findIndex((s) => s.id === seq.id),
          );
          return combined;
        }),
      );

    this.form
      .get('learningUnits')
      ?.setValue(this.selectedLearningUnits.map((s) => s.id));

    const consumerQuery = { sort: 'name' };
    this.consumers$ = from(
      this.consumerService.ensureLoaded(consumerQuery),
    ).pipe(
      switchMap(() =>
        this.store.select(this.consumerService.allByQuery(consumerQuery)),
      ),
    );

    const subjectQuery = { sort: 'name' };
    this.subjects$ = from(this.subjectService.ensureLoaded(subjectQuery)).pipe(
      switchMap(() =>
        this.store.select(this.subjectService.allByQuery(subjectQuery)),
      ),
    );

    const learningSectionLayoutQuery = { sort: 'name' };

    this.learningSectionLayouts$ = from(
      this.learningSectionLayoutService.ensureLoaded(
        learningSectionLayoutQuery,
      ),
    ).pipe(
      switchMap(() =>
        this.store.select(
          this.learningSectionLayoutService.allByQuery(
            learningSectionLayoutQuery,
          ),
        ),
      ),
      tap((layouts) => {
        if (layouts.length && !this.form.get('learningSectionLayout')?.value) {
          this.form.patchValue({
            learningSectionLayout: layouts[0].id,
          });
        }
      }),
    );
  }

  receiveLearningUnitData(selected: LearningUnitEntity[] | null) {
    this.selectedLearningUnits = selected ?? [];
    this.form
      .get('learningUnits')
      ?.setValue(this.selectedLearningUnits.map((s) => s.id));
  }

  async receiveLearningUnitQuery(query: string) {
    this.searchQueryLearningUnits = query?.trim() || null;

    if (
      this.searchQueryLearningUnits &&
      this.searchQueryLearningUnits.length > 3
    ) {
      const searchParams = {
        'filter[idOrTitleLike]': this.searchQueryLearningUnits,
      };
      await this.learningUnitService.ensureLoaded(searchParams);

      this.learningUnits$ = this.store
        .select(this.learningUnitService.allByQuery(searchParams))
        .pipe(map((results) => results));
    }
    else {
      const learningUnitPageQuery = {
        'page[size]': this.pageSizeLearningUnits,
        'page[number]': this.currentPageLearningUnits,
        'sort': 'title',
      };
      this.learningUnits$ = this.store
        .select(this.learningUnitService.allByQuery(learningUnitPageQuery))
        .pipe(
          map((paginated) => {
            const combined = [
              ...this.selectedLearningUnits,
              ...paginated,
            ].filter(
              (lu, index, self) =>
                index === self.findIndex((s) => s.id === lu.id),
            );
            return combined;
          }),
        );
    }
  }

  async loadMoreLearningUnits(event: { pageNumber: number, pageSize: number }) {
    if (!this.searchQueryLearningUnits) {
      const learningUnitPageQuery = {
        'page[size]': event.pageSize,
        'page[number]': event.pageNumber,
        'sort': 'title',
      };

      await this.learningUnitService.ensureLoaded(learningUnitPageQuery);

      const currentLearningUnits = await firstValueFrom(
        this.learningUnits$ ?? of([]),
      );

      this.learningUnits$ = this.store
        .select(this.learningUnitService.allByQuery(learningUnitPageQuery))
        .pipe(
          map((paginated) => {
            const combined = [
              ...this.selectedLearningUnits,
              ...currentLearningUnits,
              ...paginated,
            ].filter(
              (lu, index, self) =>
                index === self.findIndex((s) => s.id === lu.id),
            );
            return combined;
          }),
        );
    }
  }

  private translocoService = inject(TranslocoService);

  public get learningUnitLabel(): string {
    if (!this.selectedLearningUnits || !this.selectedLearningUnits.length) {
      return this.translocoService.translate('learning_units_id_or_title');
    }
    return this.selectedLearningUnits.map((lu) => lu.title).join(', ');
  }

  save(): void {
    if (this.form.valid) {
      const formValue = this.form.value;

      const updatedLearningSection = Object.assign(
        new LearningSectionEntity(),
        { id: this.data?.id },
        formValue,
        {
          consumer: formValue.consumer ?
            { type: 'consumers', id: formValue.consumer } :
            null,
          subjects: (formValue.subjects as string[])?.length ?
            (formValue.subjects as string[]).map((id) => ({ id })) :
            [],
          learningUnits: (formValue.learningUnits as string[])?.length ?
            (formValue.learningUnits as string[]).map((id) => ({ id })) :
            [],
          learningSectionLayout: formValue.learningSectionLayout ?
            {
              type: 'learning-section-layouts',
              id: formValue.learningSectionLayout,
            } :
            null,
          learningSectionContainers: (
            formValue.learningSectionContainers as (
              | string |
              RelationRef<LearningSectionContainerEntity>
            )[]
          )?.length ?
            (
              formValue.learningSectionContainers as (
                | string |
                RelationRef<LearningSectionContainerEntity>
              )[]
            ).map((c) =>
              typeof c === 'string' ?
                { id: c, type: 'learning-section-containers' } :
                c,
            ) :
            [],
        },
      );
      this.dialogRef.close(updatedLearningSection);
    }
  }

  cancel(): void {
    this.dialogRef.close();
  }

  trackById(index: number, item: LearningUnitEntity) {
    return item.id;
  }
}
